import moment from "moment";
import React, { useEffect, useState, useMemo, CSSProperties } from "react";
import CardBillingDepositHistoryUX from "react-lib/apps/HISV3/BIL/CardBillingDepositHistoryUX";
import CardBillingDepositHistoryDetailUX from "react-lib/apps/HISV3/BIL/CardBillingDepositHistoryDetailUX";
import ModInfo from "react-lib/apps/common/ModInfo";
import { formatComma } from "react-lib/utils/tsUtils";
import { Button } from "semantic-ui-react";
import { useIntl } from "react-intl";
import {
  BillingDepositHistorySequence,
  BillReportPrintType,
  DepositHistoryType,
  ReceiptItem,
  ReceiptMode,
  RunSequence,
  SetProp,
} from "./sequence/BillingDepositHistory";
import { formatDatetime } from "react-lib/utils/dateUtils";

type CardBillingDepositHistoryProps = {
  // function
  onEvent: (e: any) => void;
  setProp: SetProp;
  runSequence: RunSequence;

  // seq
  BillingDepositHistorySequence?: BillingDepositHistorySequence;
  billReportPrint?: BillReportPrintType;
  languageUX?: string;
};

const AlignCenterStyle = { textAlign: "center" } as CSSProperties;
const AlignRightStyle = { textAlign: "right" } as CSSProperties;

const CardBillingDepositHistory = (props: CardBillingDepositHistoryProps) => {
  const [type, setType] = useState("mode");
  const intl = useIntl();
  useEffect(() => {
    props.runSequence({
      sequence: "BillingDepositHistory",
      restart: true,
    });
  }, []);

  const handlePrintReceipt = (payment_receipt_id: string) => {
    props.runSequence({
      sequence: "BillingDepositHistory",
      action: "printReceipt",
      receipt: payment_receipt_id,
    });
  };

  const handleCloseModCancel = () => {
    props.setProp("billReportPrint", {});
  };

  const handleCancelReportPrint = () => {
    props.onEvent({
      message: "HandleReportPrintTrackingResult",
      params: {
        action: "CANCEL",
        taskId: props.billReportPrint?.task_id,
      },
    });
  };

  const handleSelectDepositHistory = (item: DepositHistoryType) => {
    props.onEvent({
      message: "RunSequence",
      params: {
        sequence: "BillingDepositHistory",
        action: "getDepositHistoryDetail",
        selectDeposit: item,
      },
    });

    setType("mode");
  };

  const depositHistoryItems = useMemo(() => {
    return (props.BillingDepositHistorySequence?.depositHistory || []).map(
      (item: DepositHistoryType) => ({
        ...item,
        paymentDatetime: <div style={AlignCenterStyle}>{item?.payment_datetime}</div>,
        paymentReceipt: <div style={AlignCenterStyle}>{item?.payment_receipt_no}</div>,
        depositReceipt: <div style={AlignCenterStyle}>{item?.deposit_code}</div>,
        encounterType: <div style={AlignCenterStyle}>{item?.payment_encounter_type}</div>,
        payment: <div style={AlignRightStyle}>{formatComma(item?.value || "0.00")}</div>,
        paymentCashier: <div style={AlignCenterStyle}>{item?.payment_cashier}</div>,
        buttonDetail: (
          <div style={AlignCenterStyle}>
            <Button color="blue" onClick={() => handleSelectDepositHistory(item)}>
              รายละเอียด
            </Button>
          </div>
        ),
        buttonPrint: (
          <div style={AlignCenterStyle}>
            <Button color="blue" onClick={() => handlePrintReceipt(item?.payment_receipt_id || "")}>
              พิมพ์ใบแสดงรายละเอียด
            </Button>
          </div>
        ),
      })
    );
  }, [props.BillingDepositHistorySequence?.depositHistory]);

  const receiptItem = useMemo(() => {
    return (props.BillingDepositHistorySequence?.selectDepositHistory?.receiptItem || []).map(
      (item: ReceiptItem) => {
        const formattedItem: any = {
          ...item,
          edited: <div>{formatDatetime(item?.edited)}</div>,
          discount_code: <div style={AlignRightStyle}>{item?.discount_code}</div>,
        };

        formatRightFields.forEach((key:string) => {
          const rawValue = (item as any)[key] ?? "0.00";
          const value = key === "quantity" ? parseFloat(rawValue) : formatComma(rawValue);
          formattedItem[key] = <div style={AlignRightStyle}>{value}</div>;
        });

        return formattedItem;
      }
    );
  }, [props.BillingDepositHistorySequence?.selectDepositHistory?.receiptItem]);

  const receiptMode = useMemo(() => {
  return (props.BillingDepositHistorySequence?.selectDepositHistory?.receiptMode || []).map(
    (item: ReceiptMode) => {
      const formattedItem: Record<string, any> = { ...item };

      formatFields.forEach((key) => {
        formattedItem[key] = (
          <div style={AlignRightStyle}>{formatComma(item?.[key] ?? "0.00")}</div>
        );
      });

      return formattedItem;
    }
  );
}, [props.BillingDepositHistorySequence?.selectDepositHistory?.receiptMode]);

  const handleChangeDate = (date: string, name: "startDate" | "endDate") => {
    props.setProp(`BillingDepositHistorySequence.${name}`, date);
  };

  const handleChange = (e: any, v: any) => {
    props.setProp(
      `BillingDepositHistorySequence.${v?.name as keyof BillingDepositHistorySequence}`,
      v?.value
    );
  };

  const onSearchDepositHistory = () => {
    props.onEvent({
      message: "RunSequence",
      params: {
        sequence: "BillingDepositHistory",
        action: "getDepositHistory",
      },
    });
  };

  const handleChangeType = (e: any, v: any) => {
    setType(v.name);
  };

  const handleCloseDetail = () => {
    props.setProp("BillingDepositHistorySequence.selectDepositHistory", {});
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <CardBillingDepositHistoryUX
        depositHistory={props.BillingDepositHistorySequence}
        startDate={props.BillingDepositHistorySequence?.startDate}
        endDate={props.BillingDepositHistorySequence?.endDate}
        depositHistoryData={depositHistoryItems}
        depositTotal={props.BillingDepositHistorySequence?.depositTotal}
        onChangeStartDate={(date: any) => handleChangeDate(date, "startDate")}
        onChangeEndDate={(date: any) => handleChangeDate(date, "endDate")}
        onChange={handleChange}
        onSearchDepositHistory={() => onSearchDepositHistory()}
        languageUX={props.languageUX}
      />

      {props.BillingDepositHistorySequence?.selectDepositHistory?.id && (
        <CardBillingDepositHistoryDetailUX
          selectDepositHistory={props.BillingDepositHistorySequence?.selectDepositHistory}
          type={type}
          receiptItem={receiptItem}
          receiptMode={receiptMode}
          onChangeType={handleChangeType}
          onCloseDetail={handleCloseDetail}
          languageUX={props.languageUX}
        />
      )}

      <ModInfo
        open={!!props.billReportPrint?.open}
        titleColor={"black"}
        titleName={intl.formatMessage({ id: "กำลังเตรียมเอกสาร" })}
        btnText={intl.formatMessage({ id: "ยกเลิก" })}
        buttonColor="red"
        style={{ width: "27.5%" }}
        onApprove={handleCancelReportPrint}
        // onClose={handleCloseModInfo}
      >
        <div
          style={{
            padding: "0.5rem 0",
            lineHeight: 1.75,
            textAlign: "left",
          }}
        >
          <div>
            กรุณารอสักครู่
            <br />
            {!!props.billReportPrint?.status && (
              <>
                Status: {props.billReportPrint?.status} {props.billReportPrint?.progress}%
                <br />
              </>
            )}
            For IT: task_id = {props.billReportPrint?.task_id}
          </div>
        </div>
      </ModInfo>

      <ModInfo
        open={!!props.billReportPrint?.error}
        titleColor={"red"}
        onApprove={handleCloseModCancel}
        onClose={handleCloseModCancel}
      >
        <div
          style={{
            padding: "0.5rem 0",
            lineHeight: 1.75,
            textAlign: "left",
          }}
        >
          {props.billReportPrint?.error}
        </div>
      </ModInfo>
    </div>
  );
};

export default CardBillingDepositHistory;

const formatRightFields = [
  "quantity",
  "unitprice",
  "price",
  "send_claim",
  "absorb",
  "claimable",
  "non_claimable",
  "discount",
  "payable",
  "drg_discount_amount",
];

const formatFields: (keyof ReceiptMode)[] = [
    "price__sum",
    "send_claim__sum",
    "absorb__sum",
    "patient_claim__sum",
    "non_claimable__sum",
    "discount__sum",
    "payable__sum",
  ];
